<script>
/**
 * CocinareporteinterrelacionQuicktable
 * 
 * @author Daniel Lepe
 * @date May 22th, 2025
 * @version 1.0
 */
import _ from 'lodash'
import { objectToUrlString } from '@/utils.js'
import { captions, quicktableFilters, baseIndex as fetchingAPI } from './archetype.js'
// import moduleNavigator from '../moduleNavigator.vue'

import QuickCardFooter from '@/views/elements/quick-card-footer.vue'
import axios from 'axios'
import automodal from './CocinareporteinterrelacionAutomodal.vue'

let name = 'CocinareporteinterrelacionQuicktable',
	newButtonLabel = 'New',
	emptyHeader = 'Data not found',
	emptyLead = 'Module empty',
	reloadData = 'Try reloading this page',
	tryAgainLater = '',
	header = null,
	footer = null;

function processItems(items) {
	// Group items by usuario_id_pk
	return _.chain(items)
		.groupBy('usuario_id_pk')
		.map((userItems, userId) => {
			const firstItem = userItems[0];

			// Get all plans for this user and calculate subtotal
			const planesChained = _.chain(userItems)
				.groupBy('plan')
				.mapValues(planItems => ({
					cantidad: _.sumBy(planItems, item => parseInt(item.cantidad)),
					precio: _.sumBy(planItems, item => parseInt(item.precio))
				}))
				.value();

			let planes = {},
				precio = 0;
			_.forEach(planesChained, (pChain, val) => {
				planes[val] = pChain.cantidad;
				precio += (pChain.precio);
			})

			// Return formatted user object
			return {
				usuario_id_pk: userId,
				cliente: firstItem.cliente,
				sede: firstItem.sede,
				sede_tipo: firstItem.sede_tipo,
				codigo: firstItem.codigo,
				imagen_logo: firstItem.imagen_logo,
				precio,
				...planes,
			};
		})
		.value();
}

export default {
	name,

	components: {
		automodal,
		QuickCardFooter,
		// moduleNavigator
	},

	props: {
		columnizePlanes: {
			type: Boolean,
			default: false
		},
		hideSede: {
			type: Boolean,
			default: false
		},
		hideFilters: {
			type: Boolean,
			default: false
		},
		hidePlanName: {
			type: Boolean,
			default: false
		},
		value: {
			type: [Object, Number, String]
		},
		valueName: {
			type: String,
			default: 'id'
		},
		disableNew: {
			type: Boolean,
			default: true
		},
		selectMode: {
			type: Boolean,
			default: false
		},
		hideNavigation: {
			type: Boolean,
			default: false
		},
		fetchingAPI: {
			type: String,
			default: () => { return fetchingAPI }
		},
		header: {
			type: String,
			default: () => { return header }
		},
		perPage: {
			type: Number,
			default: 1000,
		},
		footer: {
			type: String,
			default: () => { return footer }
		},
		captions: {
			type: [Array, Object],
			default: () => captions
		},
		args: {
			type: Object,
			default: () => { return {} }
		}
	},

	computed: {
		getPlansSums() {
			const items = _.cloneDeep(this.items)

			// First group items by plan_id and sum their quantities
			const planTotals = _.groupBy(items, 'plan');

			// Create unique plans with subtotals
			return _.uniqBy(items.map(item => ({
				plan: item.plan,
				subtotal: _.sumBy(planTotals[item.plan], item => parseInt(item.cantidad)),
				subtotalPrecio: _.sumBy(planTotals[item.plan], item => parseInt(item.precio))
			})), 'plan');
		},
		cCaptions() {
			let r = [];
			try {
				if (this.selectMode) {
					r.push({
						key: 'check',
						label: ''
					})
				}
				_.forEach(this.captions, item => {
					switch (item.key) {
						case 'precio':
							break;
						case 'sede':
							if (!this.hideSede)
								r.push(item);
							break;
						case 'grupo':
							if (this.hideSede)
								r.push(item);
							break;
						case 'cantidad':
							if (!this.columnizePlanes)
								r.push(item);
							break;
						case 'plan':
							if (!this.hidePlanName && !this.columnizePlanes)
								r.push(item);
							break;
						default:
							r.push(item)
							break;
					}
				})
				if (this.columnizePlanes)
					_.forEach(this.getPlansSums, Plan => {
						r.push({ key: Plan.plan, sortable: true })
					})
				_.forEach(this.captions, item => {
					switch (item.key) {
						case 'precio':
							r.push(item);
							break;
					}
				})
			} catch (e) {
				console.error(e)
			}
			return r;
		},
		sourceURI() {
			let init = this.fetchingAPI + "/" + this.perPage + "/" + this.currentPage;
			try {
				if (this.term) {
					init += "/" + this.term;
				} else if (!this.term || this.term == "s") {
					init += "/@";
				}
				const fullArgs = _.merge(this.args, this.filters);
				init += '/' + objectToUrlString(fullArgs)
				return init.trim().replace(/[ ]/g, '_');
			} catch (e) {
				console.warn("sourceURI.building.error", init, e)
			}
			return init;
		},
		itemsGroupedByPlan() {
			return processItems(this.items)
		}
	},

	data: () => {
		return {
			name, newButtonLabel, emptyHeader,
			emptyLead, tryAgainLater, reloadData,
			currentPage: 1,
			items: [],
			groupingDefinitions: {},
			itemsCount: 0,
			term: null, filters: {},
			isLoading: false
		}
	},

	methods: {
		isSelected(data) {
			// console.log('isSelected', data, this.value, (this.value == data.item[this.valueName]))
			return (this.value == data.item[this.valueName])
		},
		rowClicked(item, index, event) {
			this.$emit('setItem', item, event)
			this.$nextTick(() => {
				if (this.selectMode)
					this.$emit('input', item[this.valueName], { item, index, event })
			})
		},
		quicktableFilters,
		async refreshFromStart() {
			this.currentPage = 1
			this.refresh()
		},
		async refresh() {
			this.isLoading = true
			await this.loadUp()
			this.isLoading = false
		},
		async loadUp() {
			try {
				let response = await axios.get(this.sourceURI)
				this.items = response.data.rows;
				this.itemsCount = response.data.count;
				this.groupingDefinitions = (_.isObjectLike(response.groupingDefinitions) ? response.groupingDefinitions : {});
			} catch (error) {
				this.items = [];
				this.itemsCount = 0;
				console.error(error)
			}
		}
	},

	watch: {
		currentPage(a, b) {
			this.refresh();
		},
		'$route'() {
			this.loadUp()
		}
	},

	mounted() {
		this.refresh()
	}
};
</script>

<template>
	<div :id="name">

		<!-- <module-navigator class="mb-3" v-if="!hideNavigation" /> -->

		<b-card no-body>

			<!-- TABLE CONTENT -->
			<b-table responsive class="animated fadeIn" v-if="items.length && !isLoading" @row-clicked="rowClicked"
				:hover="selectMode" 
				:items="(!columnizePlanes) ? items : itemsGroupedByPlan" 
				:fields="cCaptions">

				<template #cell(check)="data">
					<div>
						<input type="checkbox" :checked="isSelected(data)" @click.prevent="() => { }" />
					</div>
				</template>

				<template #cell(sede)="data">
					<div class="nobreak">
						<b>{{ data.value }}</b>
					</div>
					<div class="nobreak" v-if="data.item.grupo">
						<i>{{ data.item.grupo }}</i>
					</div>
				</template>

				<template #cell(cliente)="data">
					<b>{{ data.value }}</b>
					<template v-if="data.item.notas_alimentarias">
						<br>
						<i class="notas_alimentarias">{{ data.item.notas_alimentarias }}</i>
					</template>
				</template>

				<template #cell(cantidad)="data">
					<div class="nobreak">
						<strong>{{ data.value }} {{ parseInt(data.value) === 1 ? 'Unidad' : 'Unidades' }}</strong>
					</div>
				</template>

				<template #cell(precio)="data">
					{{ data.value | currency }}
				</template>

				<template #cell(imagenes_id)="data">
					<api-img-pristine :comp="data.value" size="128/128" :thumbnail="true" :allow-zoom="true" />
				</template>

				<template #cell(_)="data">
					<automodal :concept-id="data.item.id" @refresh="refresh" :args="args">
						Editar
					</automodal>
				</template>

				<template #bottom-row v-if="!columnizePlanes">
					<b-td :colspan="cCaptions.length - 2" class="text-right">
						<strong>Total:</strong>
					</b-td>
					<b-td>
						<strong>{{ items.reduce((sum, item) => sum + parseInt(item.cantidad), 0) }} Unidades</strong>
					</b-td>
					<b-td>
						<strong>{{ items.reduce((sum, item) => sum + parseInt(item.precio), 0) | currency }}</strong>
					</b-td>
				</template>
			</b-table>

			<!-- LOADING SPINNER -->

			<div v-if="isLoading">
				<div class="text-center p-5">
					<b-spinner variant="primary" />
				</div>
			</div>

			<!-- EMPTY JUMBOTRON -->

			<div v-else-if="!items.length" class="pt-4 pl-4 pr-4 pb-0">
				<b-jumbotron :header="emptyHeader" :lead="emptyLead">
					<p>{{ tryAgainLater }}</p>
					<b-button variant="primary" @click="refresh()">{{ reloadData }}</b-button>
				</b-jumbotron>
			</div>

			<!-- HEADER -->

			<template v-slot:header>
				<b-row align-h="between">

					<b-col md="auto" v-if="!!header">
						<h6 class="mt-2 mb-2">{{ header }}</h6>
					</b-col>

					<template v-if="!hideFilters">
						<b-col class="hide-on-print">
							<b-input-group>
								<b-form-input v-model="term" @keyup.enter="refreshFromStart()"
									placeholder="Filtra en los resultados" />
								<b-input-group-append>
									<b-button v-if="!!term" variant="secondary"
										@click="term = ''; refreshFromStart();"><i class="fa fa-times"></i></b-button>
									<b-button variant="secondary" @click="refreshFromStart()" :disabled="!term"><i
											class="fa fa-search"></i></b-button>
								</b-input-group-append>
							</b-input-group>
						</b-col>
						<quicktable-filters v-model="filters" @changed="refreshFromStart" :filters="quicktableFilters"
							:groupingDefinitions="groupingDefinitions" class="hide-on-print" />
					</template>

					<b-col md="auto">
						<download-as-csv-button 
							:content="(!columnizePlanes) ? items : itemsGroupedByPlan" 
							:headers="cCaptions" 
							:file-name="header" />
					</b-col>

					<b-col md="auto" class="align-right">
						<automodal @refresh="refresh" :args="args" v-if="!disableNew">
							Nuevo
						</automodal>
					</b-col>

				</b-row>
			</template>

			<template v-slot:footer v-if="!isLoading && items.length">
				<quick-card-footer v-model="currentPage" class="hide-on-print" :items-count="itemsCount"
					:per-page="perPage" :items-fetched="items.length" @refresh="refresh" />
			</template>

		</b-card>

	</div>
</template>

<style lang="scss">
#CocinareporteinterrelacionQuicktable {
	.notas_alimentarias {
		background-color: var(--light);
		color: var(--danger);
		font-size: 0.75em;
		border-radius: 0.25em;
	}

}
</style>